// src/store/slices/bookingWizardSlice.ts
import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import type {
  Vehicle,
  TripDetails,
  ReturnTripDetails,
  CustomerInfo,
  SpecialRequests,
  TransferType,
  ServiceType,
  PromoCode,
  CarSeats,
  ChildrenAges,
  Booking
} from "@/types";

// Define the state interface
interface BookingWizardState {
  currentStep: number;
  transferType: TransferType;
  serviceType: ServiceType;
  primaryTrip: TripDetails;
  returnTrip?: ReturnTripDetails;
  passengers: {
    adults: number;
    children: number;
    bags: number;
  };
  selectedVehicle: Vehicle | null;
  customerInfo: CustomerInfo;
  bookingStatus: "draft" | "pending" | "confirmed" | "completed" | "cancelled";
  paymentStatus: "pending" | "processing" | "paid" | "failed";
  specialRequests: SpecialRequests;
  carSeats?: CarSeats;
  childrenAges?: ChildrenAges;
  promoCode?: PromoCode;
  paymentInProgress: boolean;
  errors: {
    [key: string]: string | null;
  };
  meta: {
    lastUpdated: string;
    totalSteps: number;
    completedSteps: string[];
    isModified: boolean;
  };
}

// Define initial state
const initialState: BookingWizardState = {
  currentStep: 0,
  transferType: "Airport Transfer",
  serviceType: "Round Trip",
  primaryTrip: {
    pickupDate: "",
    pickupTime: "",
    pickupLocation: null,
    dropoffLocation: null,
    pickupIsAirport: true
  },
  passengers: {
    adults: 2,
    children: 0,
    bags: 0
  },
  selectedVehicle: null,
  customerInfo: {
    firstName: "",
    lastName: "",
    email: "",
    phone: "",
    country: "",
    city: ""
  },
  bookingStatus: "draft",
  paymentStatus: "pending",
  specialRequests: {
    childSeat: false,
    handicapAccess: false,
    extraLuggage: false,
    notes: ""
  },
  carSeats: undefined,
  childrenAges: undefined,
  paymentInProgress: false,
  errors: {},
  meta: {
    lastUpdated: new Date().toISOString(),
    totalSteps: 4,
    completedSteps: [],
    isModified: false
  }
};

export const bookingWizardSlice = createSlice({
  name: "bookingWizard",
  initialState,
  reducers: {
    // Navigation
    setStep: (state, action: PayloadAction<number>) => {
      state.currentStep = action.payload;
      state.meta.lastUpdated = new Date().toISOString();
    },

    // Service Type Selection
    setTransferType: (state, action: PayloadAction<TransferType>) => {
      state.transferType = action.payload;
      // Reset locations when changing transfer type
      state.primaryTrip.pickupLocation = null;
      state.primaryTrip.dropoffLocation = null;
      if (state.returnTrip) {
        state.returnTrip.pickupLocation = null;
        state.returnTrip.dropoffLocation = null;
      }
      state.meta.isModified = true;
    },

    setServiceType: (state, action: PayloadAction<ServiceType>) => {
      state.serviceType = action.payload;
      // Clear return trip if switching to One Way
      if (action.payload === "One Way") {
        state.returnTrip = undefined;
      }
      state.meta.isModified = true;
    },

    // Trip Details
    setPrimaryTripDetails: (state, action: PayloadAction<Partial<TripDetails>>) => {
      state.primaryTrip = { ...state.primaryTrip, ...action.payload };
      state.meta.isModified = true;
    },

    setReturnTripDetails: (state, action: PayloadAction<Partial<ReturnTripDetails>>) => {
      if (!state.returnTrip) {
        state.returnTrip = {
          pickupDate: "",
          pickupTime: "",
          pickupLocation: null,
          dropoffLocation: null,
          flightInfo: undefined
        };
      }
      state.returnTrip = { ...state.returnTrip, ...action.payload };
      state.meta.isModified = true;
    },

    // Passenger Details
    setPassengers: (state, action: PayloadAction<Partial<BookingWizardState["passengers"]>>) => {
      state.passengers = { ...state.passengers, ...action.payload };
      state.meta.isModified = true;
    },

    // Vehicle Selection
    setVehicle: (state, action: PayloadAction<Vehicle>) => {
      state.selectedVehicle = action.payload;
      state.meta.isModified = true;
    },

    // Customer Information
    setCustomerInfo: (state, action: PayloadAction<Partial<CustomerInfo>>) => {
      state.customerInfo = { ...state.customerInfo, ...action.payload };
      state.meta.isModified = true;
    },

    // Booking Status
    setBookingStatus: (state, action: PayloadAction<BookingWizardState["bookingStatus"]>) => {
      state.bookingStatus = action.payload;
      state.meta.lastUpdated = new Date().toISOString();
    },

    // Payment Status
    setPaymentStatus: (state, action: PayloadAction<BookingWizardState["paymentStatus"]>) => {
      state.paymentStatus = action.payload;
      state.meta.lastUpdated = new Date().toISOString();
    },

    // Special Requests
    updateSpecialRequests: (state, action: PayloadAction<Partial<SpecialRequests>>) => {
      state.specialRequests = { ...state.specialRequests, ...action.payload };
      state.meta.isModified = true;
    },

    // Car Seats
    setCarSeats: (state, action: PayloadAction<CarSeats | undefined>) => {
      state.carSeats = action.payload;
      // Sync legacy childSeat boolean
      if (action.payload) {
        state.specialRequests.childSeat = action.payload.standard > 0 || action.payload.booster > 0;
      } else {
        state.specialRequests.childSeat = false;
      }
      state.meta.isModified = true;
    },

    // Children Ages
    setChildrenAges: (state, action: PayloadAction<ChildrenAges | undefined>) => {
      state.childrenAges = action.payload;
      state.meta.isModified = true;
    },

    // Promo Code
    applyPromoCode: (state, action: PayloadAction<PromoCode>) => {
      state.promoCode = action.payload;
      state.meta.isModified = true;
    },

    removePromoCode: (state) => {
      state.promoCode = undefined;
      state.meta.isModified = true;
    },

    // Error Handling
    setError: (state, action: PayloadAction<{ field: string; message: string | null }>) => {
      const { field, message } = action.payload;
      state.errors[field] = message;
    },

    clearErrors: (state) => {
      state.errors = {};
    },

    // Progress Tracking
    completeStep: (state, action: PayloadAction<string>) => {
      if (!state.meta.completedSteps.includes(action.payload)) {
        state.meta.completedSteps.push(action.payload);
      }
    },

    // Restore from cached booking (payment retry after Fiserv redirect)
    restoreBookingState: (state, action: PayloadAction<Booking>) => {
      const booking = action.payload;
      state.customerInfo = booking.customerInfo;
      state.selectedVehicle = booking.selectedVehicle;
      state.serviceType = booking.serviceType;
      state.transferType = booking.transferType;
      state.primaryTrip = booking.primaryTrip;
      state.returnTrip = booking.returnTrip;
      state.passengers = booking.passengers;
      state.specialRequests = booking.specialRequests;
      state.carSeats = booking.carSeats;
      state.childrenAges = booking.childrenAges;
      state.currentStep = 4; // Go directly to payment step
      state.bookingStatus = "draft";
      state.paymentStatus = "pending";
      state.meta.lastUpdated = new Date().toISOString();
      state.meta.isModified = true;
    },

    // Payment state
    setPaymentInProgress: (state, action: PayloadAction<boolean>) => {
      state.paymentInProgress = action.payload;
    },

    // Reset
    resetBookingWizard: (state, action?: PayloadAction<{ keepCustomerInfo?: boolean }>) => {
      const customerInfoToKeep = action?.payload?.keepCustomerInfo
        ? state.customerInfo
        : initialState.customerInfo;
      return {
        ...initialState,
        customerInfo: customerInfoToKeep
      };
    }
  }
});

// Export actions
export const {
  setStep,
  setTransferType,
  setServiceType,
  setPrimaryTripDetails,
  setReturnTripDetails,
  setPassengers,
  setVehicle,
  setCustomerInfo,
  setBookingStatus,
  setPaymentStatus,
  updateSpecialRequests,
  setCarSeats,
  setChildrenAges,
  applyPromoCode,
  removePromoCode,
  setError,
  clearErrors,
  completeStep,
  restoreBookingState,
  setPaymentInProgress,
  resetBookingWizard
} = bookingWizardSlice.actions;

export default bookingWizardSlice.reducer;
